import React, { useState, useEffect, useRef } from 'react';
import { Calculator, Clock, IndianRupee, Users, TrendingUp, Factory, ArrowRight, CheckCircle } from 'lucide-react';
import { roiCalculatorData } from '../../data/mock';

const ROICalculatorSection = ({ onOpenCalendar }) => {
  const [ownerHours, setOwnerHours] = useState(30);
  const [teamSize, setTeamSize] = useState(50);
  const [revenue, setRevenue] = useState(10000000); // ₹1 Crore
  const [industry, setIndustry] = useState('Manufacturing');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Get industry multiplier
  const getIndustryMultiplier = () => {
    const ind = roiCalculatorData.industries.find(i => i.name === industry);
    return ind ? ind.multiplier : 1.0;
  };

  // Calculate all ROI metrics
  const calculateROI = () => {
    const industryMultiplier = getIndustryMultiplier();
    const avgSalary = roiCalculatorData.avgSalary;
    const zenovaCost = roiCalculatorData.zenovaCost;
    const efficiencyGain = roiCalculatorData.efficiencyGain;

    // Owner time value calculation (assuming owner's time is 5x employee salary per hour)
    const ownerHourlyValue = (avgSalary * 5) / 160; // Monthly salary / 160 hours
    const weeklyOwnerTimeLoss = ownerHours * ownerHourlyValue;
    const annualOwnerTimeLoss = weeklyOwnerTimeLoss * 52;

    // Team salary cost (based on team size * avg salary)
    const teamMonthlyCost = teamSize * avgSalary;
    const teamAnnualCost = teamMonthlyCost * 12;

    // Potential revenue growth (5% increment based on systemization)
    const potentialRevenueGrowth = revenue * roiCalculatorData.revenueIncrement * industryMultiplier;

    // Hours saved per week
    const hoursSaved = Math.round(ownerHours * efficiencyGain);

    // Total value recovered
    const totalValueRecovered = Math.round(
      (annualOwnerTimeLoss * efficiencyGain) + // Owner time saved
      (teamAnnualCost * 0.15) + // 15% team efficiency improvement
      potentialRevenueGrowth // Revenue growth potential
    );

    // Net ROI after deducting Zenova cost
    const netROI = totalValueRecovered - zenovaCost;
    const roiMultiple = Math.round((totalValueRecovered / zenovaCost) * 10) / 10;

    return {
      ownerHourlyValue: Math.round(ownerHourlyValue),
      annualOwnerTimeLoss: Math.round(annualOwnerTimeLoss),
      hoursSaved,
      teamAnnualCost,
      potentialRevenueGrowth: Math.round(potentialRevenueGrowth),
      totalValueRecovered,
      zenovaCost,
      netROI,
      roiMultiple
    };
  };

  const roi = calculateROI();

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(1)}K`;
    } else {
      return `₹${amount.toLocaleString('en-IN')}`;
    }
  };

  const formatRevenue = (val) => {
    if (val >= 10000000) {
      return `₹${(val / 10000000).toFixed(1)} Cr`;
    } else if (val >= 100000) {
      return `₹${(val / 100000).toFixed(0)} L`;
    }
    return `₹${val.toLocaleString('en-IN')}`;
  };

  // Revenue slider settings (min 1Cr, 5% increments up to 50Cr)
  const revenueSteps = [];
  for (let i = 10000000; i <= 500000000; i += i * 0.05) {
    revenueSteps.push(Math.round(i));
  }

  // Team size in multiples of 25
  const handleTeamSizeChange = (val) => {
    const rounded = Math.round(val / 25) * 25;
    setTeamSize(Math.max(25, rounded));
  };

  return (
    <section 
      id="calculator"
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: 'var(--bg-page)' }}
    >
      <div className="absolute inset-0 bg-grid opacity-30" />
      
      <div className="container relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isVisible ? 'animate-fadeInUp' : 'opacity-0'
            }`}
            style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
          >
            <Calculator size={16} />
            <span className="font-semibold text-sm">ROI Calculator</span>
          </div>
          
          <h2 
            className={`heading-1 mb-4 ${isVisible ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}
            style={{ color: 'var(--text-primary)' }}
          >
            {roiCalculatorData.title}
          </h2>
          <p 
            className={`body-large ${isVisible ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`}
            style={{ color: 'var(--text-secondary)' }}
          >
            {roiCalculatorData.subtitle}
          </p>
        </div>

        {/* Calculator Card */}
        <div className="max-w-6xl mx-auto">
          <div 
            className={`card p-6 md:p-10 ${isVisible ? 'animate-scaleIn stagger-3' : 'opacity-0'}`}
            style={{ background: 'white' }}
          >
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Input Side */}
              <div className="space-y-6">
                <h3 className="heading-3 mb-6" style={{ color: 'var(--text-primary)' }}>
                  Tell us about your business
                </h3>
                
                {/* Owner Hours Slider */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="body-medium font-medium flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      <Clock size={18} style={{ color: 'var(--brand-dark)' }} />
                      Owner hours spent firefighting/week
                    </label>
                    <div 
                      className="flex items-center gap-2 px-4 py-2 rounded-xl"
                      style={{ background: 'var(--bg-page)' }}
                    >
                      <span className="font-bold text-lg" style={{ color: 'var(--brand-dark)' }}>
                        {ownerHours} hrs
                      </span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="60"
                    step="5"
                    value={ownerHours}
                    onChange={(e) => setOwnerHours(parseInt(e.target.value))}
                    className="w-full h-3 rounded-full appearance-none cursor-pointer roi-slider"
                    data-testid="owner-hours-slider"
                    style={{ 
                      background: `linear-gradient(to right, var(--brand-dark) 0%, var(--brand-dark) ${((ownerHours - 10) / 50) * 100}%, #e5e7eb ${((ownerHours - 10) / 50) * 100}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between mt-2 text-xs" style={{ color: 'var(--text-light)' }}>
                    <span>10 hrs</span>
                    <span>60 hrs</span>
                  </div>
                </div>

                {/* Team Size Slider */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="body-medium font-medium flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      <Users size={18} style={{ color: 'var(--brand-dark)' }} />
                      Team Size (multiples of 25)
                    </label>
                    <div 
                      className="flex items-center gap-2 px-4 py-2 rounded-xl"
                      style={{ background: 'var(--bg-page)' }}
                    >
                      <span className="font-bold text-lg" style={{ color: 'var(--brand-dark)' }}>
                        {teamSize} employees
                      </span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="25"
                    max="500"
                    step="25"
                    value={teamSize}
                    onChange={(e) => handleTeamSizeChange(parseInt(e.target.value))}
                    className="w-full h-3 rounded-full appearance-none cursor-pointer roi-slider"
                    data-testid="team-size-slider"
                    style={{ 
                      background: `linear-gradient(to right, var(--brand-dark) 0%, var(--brand-dark) ${((teamSize - 25) / 475) * 100}%, #e5e7eb ${((teamSize - 25) / 475) * 100}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between mt-2 text-xs" style={{ color: 'var(--text-light)' }}>
                    <span>25</span>
                    <span>500</span>
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-light)' }}>
                    * Calculated at avg. salary of ₹{roiCalculatorData.avgSalary.toLocaleString('en-IN')}/month
                  </p>
                </div>

                {/* Revenue Slider */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="body-medium font-medium flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      <TrendingUp size={18} style={{ color: 'var(--brand-dark)' }} />
                      Annual Revenue (min ₹1 Cr)
                    </label>
                    <div 
                      className="flex items-center gap-2 px-4 py-2 rounded-xl"
                      style={{ background: 'var(--bg-page)' }}
                    >
                      <span className="font-bold text-lg" style={{ color: 'var(--brand-dark)' }}>
                        {formatRevenue(revenue)}
                      </span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="10000000"
                    max="500000000"
                    step="5000000"
                    value={revenue}
                    onChange={(e) => setRevenue(parseInt(e.target.value))}
                    className="w-full h-3 rounded-full appearance-none cursor-pointer roi-slider"
                    data-testid="revenue-slider"
                    style={{ 
                      background: `linear-gradient(to right, var(--brand-dark) 0%, var(--brand-dark) ${((revenue - 10000000) / 490000000) * 100}%, #e5e7eb ${((revenue - 10000000) / 490000000) * 100}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between mt-2 text-xs" style={{ color: 'var(--text-light)' }}>
                    <span>₹1 Cr</span>
                    <span>₹50 Cr</span>
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-light)' }}>
                    * Growth calculated at 5% increment with systemization
                  </p>
                </div>

                {/* Industry Dropdown */}
                <div>
                  <label className="body-medium font-medium flex items-center gap-2 mb-3" style={{ color: 'var(--text-primary)' }}>
                    <Factory size={18} style={{ color: 'var(--brand-dark)' }} />
                    Select Your Industry
                  </label>
                  <select
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none appearance-none cursor-pointer"
                    data-testid="industry-select"
                    style={{ 
                      borderColor: 'rgba(0, 69, 52, 0.2)',
                      background: 'var(--bg-page)'
                    }}
                  >
                    {roiCalculatorData.industries.map((ind) => (
                      <option key={ind.name} value={ind.name}>
                        {ind.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Results Side */}
              <div 
                className="rounded-3xl p-6 md:p-8 flex flex-col"
                style={{ background: 'var(--brand-dark)' }}
              >
                <div className="text-center mb-6">
                  <p className="body-medium mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Your Potential Annual Savings
                  </p>
                  <div 
                    className="display-large mb-2"
                    style={{ color: 'var(--brand-primary)' }}
                    data-testid="total-savings"
                  >
                    {formatCurrency(roi.totalValueRecovered)}
                  </div>
                  <p className="body-small" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    after Zenova systemization
                  </p>
                </div>

                <div className="space-y-3 mb-6 flex-grow">
                  <div 
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ background: 'rgba(255, 255, 255, 0.1)' }}
                  >
                    <span className="body-small flex items-center gap-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      <Clock size={16} /> Owner time saved
                    </span>
                    <span className="font-bold" style={{ color: 'var(--brand-primary)' }}>
                      {roi.hoursSaved} hrs/week
                    </span>
                  </div>
                  <div 
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ background: 'rgba(255, 255, 255, 0.1)' }}
                  >
                    <span className="body-small flex items-center gap-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      <IndianRupee size={16} /> Owner time value recovered
                    </span>
                    <span className="font-bold" style={{ color: 'var(--brand-primary)' }}>
                      {formatCurrency(roi.annualOwnerTimeLoss * roiCalculatorData.efficiencyGain)}
                    </span>
                  </div>
                  <div 
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ background: 'rgba(255, 255, 255, 0.1)' }}
                  >
                    <span className="body-small flex items-center gap-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      <TrendingUp size={16} /> Revenue growth potential
                    </span>
                    <span className="font-bold" style={{ color: 'var(--brand-primary)' }}>
                      {formatCurrency(roi.potentialRevenueGrowth)}
                    </span>
                  </div>
                  <div 
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ background: 'rgba(255, 255, 255, 0.1)' }}
                  >
                    <span className="body-small" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Zenova investment
                    </span>
                    <span className="font-bold" style={{ color: 'white' }}>
                      {formatCurrency(roi.zenovaCost)}
                    </span>
                  </div>
                  <div 
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ background: 'rgba(211, 255, 98, 0.2)' }}
                  >
                    <span className="body-small font-semibold flex items-center gap-2" style={{ color: 'var(--brand-primary)' }}>
                      <CheckCircle size={16} /> Net ROI (after Zenova cost)
                    </span>
                    <span className="font-bold text-lg" style={{ color: 'var(--brand-primary)' }}>
                      {roi.roiMultiple}x return
                    </span>
                  </div>
                </div>

                <div 
                  className="p-4 rounded-xl mb-6 text-center"
                  style={{ background: 'rgba(211, 255, 98, 0.15)' }}
                >
                  <p className="body-small" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Net Annual Gain
                  </p>
                  <p className="heading-2" style={{ color: 'var(--brand-primary)' }} data-testid="net-roi">
                    {formatCurrency(roi.netROI)}
                  </p>
                </div>

                <button
                  onClick={onOpenCalendar}
                  className="btn-accent w-full justify-center"
                  data-testid="roi-calculator-cta"
                >
                  Get Your Customized Plan
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .roi-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: var(--brand-primary);
          cursor: pointer;
          border: 4px solid var(--brand-dark);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          transition: transform 0.2s;
        }
        .roi-slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
        }
        .roi-slider::-moz-range-thumb {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: var(--brand-primary);
          cursor: pointer;
          border: 4px solid var(--brand-dark);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </section>
  );
};

export default ROICalculatorSection;
